import { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { useXRPLContext } from './xrpl-provider';
import { useAuth } from './auth';

export type LoadingStep = 'auth' | 'xrpl' | 'orderbook' | 'complete';

interface AppLoadingContextType {
  isAppReady: boolean;
  currentStep: LoadingStep;
  completedSteps: LoadingStep[];
}

const AppLoadingContext = createContext<AppLoadingContextType | undefined>(undefined);

export function AppLoadingProvider({ children }: { children: ReactNode }) {
  const { isConnected } = useXRPLContext();
  const { loading: authLoading } = useAuth();
  const [currentStep, setCurrentStep] = useState<LoadingStep>('auth');
  const [completedSteps, setCompletedSteps] = useState<LoadingStep[]>([]);
  const [isAppReady, setIsAppReady] = useState(false);

  // Use refs to track completion to avoid infinite loops
  const authCompleteRef = useRef(false);
  const xrplCompleteRef = useRef(false);
  const orderbookCompleteRef = useRef(false);

  useEffect(() => {
    // Step 1: Auth check
    if (!authLoading && !authCompleteRef.current) {
      console.log('[AppLoading] Auth complete');
      authCompleteRef.current = true;
      setCompletedSteps(['auth']);
      setCurrentStep('xrpl');
    }
  }, [authLoading]);

  useEffect(() => {
    // Step 2: XRPL connection
    console.log('[AppLoading] XRPL check:', {
      authComplete: authCompleteRef.current,
      isConnected,
      xrplComplete: xrplCompleteRef.current
    });

    if (authCompleteRef.current && isConnected && !xrplCompleteRef.current) {
      console.log('[AppLoading] XRPL complete');
      xrplCompleteRef.current = true;
      setCompletedSteps(['auth', 'xrpl']);
      setCurrentStep('orderbook');

      // Simulate orderbook loading
      const timer = setTimeout(() => {
        if (!orderbookCompleteRef.current) {
          console.log('[AppLoading] Orderbook complete');
          orderbookCompleteRef.current = true;
          setCompletedSteps(['auth', 'xrpl', 'orderbook']);
          setCurrentStep('complete');

          // Mark app as ready after all steps complete
          setTimeout(() => {
            console.log('[AppLoading] App ready');
            setIsAppReady(true);
          }, 300);
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [authCompleteRef.current, isConnected]);

  return (
    <AppLoadingContext.Provider value={{ isAppReady, currentStep, completedSteps }}>
      {children}
    </AppLoadingContext.Provider>
  );
}

export function useAppLoading() {
  const context = useContext(AppLoadingContext);
  if (context === undefined) {
    throw new Error('useAppLoading must be used within AppLoadingProvider');
  }
  return context;
}
